/*
 * BSF Cart Upsell — theme surface for the BSF_UPSELL system.
 * ---------------------------------------------------------------------------
 * Renders complement offers on the cart page + mini-cart from the SAME per-product
 * slate the checkout extension uses (product.metafields.bsf_upsell.offers → the
 * `checkout` placement, full price). Decoupled from the theme's cart engine via
 * jQuery ajaxComplete (like free-shipping-bar.js) — no dependency on app(.min).js.
 *
 * MEASUREMENT (Brad 2026-07-20 — the cart is measured on its OWN, not with checkout):
 *   - Its OWN experiment key => an INDEPENDENT randomization. Bucket key = numeric
 *     customer id when logged in, else the cart token (the theme analog of the
 *     checkout token). FNV-1a is byte-identical to BSF.Stats.Holdout; the golden
 *     vectors are pinned in UpsellOfferEngineTests (2856593585 / 2578154898).
 *   - Ground truth rides to the ORDER as cart attributes: _bsf_upsell_cart_bucket
 *     ("t"|"h", written for BOTH arms so the verdict has a denominator without
 *     needing the cart_token), _bsf_upsell_cart_imp (shown offer ids), and the
 *     accepted line carries the _bsf_upsell_cart line property.
 *   The outcome/verdict runners read these as a separate `cart` source (3/3).
 */
(function () {
  if (!window.jQuery) return;
  var $ = window.jQuery;

  // ── contract mirror (UpsellExperiment.cs — keep byte-identical) ──────────────
  var CART_EXPERIMENT_KEY = 'bsf-upsell-cart-v1';
  var CART_ROLLOUT_KEY = 'bsf-upsell-cart-rollout-v1';
  var BUCKET_ATTR = '_bsf_upsell_cart_bucket';
  var IMP_ATTR = '_bsf_upsell_cart_imp';
  var LINE_ATTR = '_bsf_upsell_cart';

  function fnv1a(s) {
    var h = 2166136261 >>> 0;
    for (var i = 0; i < s.length; i++) { h ^= (s.charCodeAt(i) & 0xff); h = Math.imul(h, 16777619) >>> 0; }
    return h >>> 0;
  }
  // Mirrors Holdout.IsHoldout: fnv1a(lower(trim(key)) + "|" + experiment) % 10000 < bps.
  function underBps(key, experiment, bps) {
    if (bps <= 0) return false;
    return fnv1a(String(key || '').trim().toLowerCase() + '|' + experiment) % 10000 < bps;
  }

  function root() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }
  function fmtMoney(dollars, format) {
    var cents = Math.round(parseFloat(dollars || '0') * 100);
    if (window.Shopify && window.Shopify.formatMoney) return window.Shopify.formatMoney(cents, format);
    return '$' + (cents / 100).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',') + ' USD';
  }

  // ── state ───────────────────────────────────────────────────────────────────
  var bucket = null;          // "t" | "h" once resolved
  var writtenBucket = null;   // last value written to the cart (dedupes /cart/update calls)
  var writtenImp = null;
  var selfUpdate = false;     // guards against reacting to our own /cart/update.js write

  function mounts() { return Array.prototype.slice.call(document.querySelectorAll('[data-bsf-cu]')); }

  function readConfig(mount) {
    var el = mount.querySelector('[data-bsf-cu-config]');
    if (!el) return null;
    try { return JSON.parse(el.textContent || 'null'); } catch (e) { return null; }
  }
  // pid -> offers[] from this mount's per-anchor <script> blocks.
  function readAnchors(mount) {
    var map = {};
    var nodes = mount.querySelectorAll('[data-bsf-cu-anchor]');
    for (var i = 0; i < nodes.length; i++) {
      var pid = String(nodes[i].getAttribute('data-pid') || '');
      if (!pid) continue;
      var arr;
      try { arr = JSON.parse(nodes[i].textContent || 'null'); } catch (e) { arr = null; }
      if (Array.isArray(arr) && arr.length) map[pid] = arr;
    }
    return map;
  }

  // Choose up to `max` offers: from anchors currently in the cart, flattened, pruned so we
  // never re-offer a variant the shopper already has (the offered product CAN still appear for
  // its OTHER variants), deduped by offered product (keep the highest-share), highest share first.
  function selectOffers(mount, cartPids, cartVids, max) {
    var anchors = readAnchors(mount);
    var inCart = {};
    for (var i = 0; i < cartPids.length; i++) inCart[String(cartPids[i])] = true;

    var byPid = {};
    Object.keys(anchors).forEach(function (apid) {
      if (!inCart[apid]) return;                       // anchor no longer in the cart
      anchors[apid].forEach(function (o0) {
        if (!o0 || o0.vid == null) return;             // un-addable
        var o = pruneVariants(o0, cartVids);           // trim/drop cart-owned variants
        if (!o) return;                                // shopper already has every variant
        var k = String(o.pid);
        if (!byPid[k] || (o.s || 0) > (byPid[k].s || 0)) byPid[k] = o;
      });
    });

    return Object.keys(byPid).map(function (k) { return byPid[k]; })
      .sort(function (a, b) { return (b.s || 0) - (a.s || 0); })
      .slice(0, Math.max(1, max || 3));
  }

  // Variant-picker options (publisher `vv`) — byte-mirror of upsell-core parse.js
  // variantOptions(): offered variant first, each margin-guarded at publish; LIVE prices.
  // Returns null unless >=2 well-formed options, in which case we render a pickerless card
  // (single-variant products need no picker — matches the checkout/TY extensions).
  function variantOptions(o) {
    var raw = o && o.vv;
    if (!Array.isArray(raw)) return null;
    var out = [];
    for (var i = 0; i < raw.length && out.length < 120; i++) {
      var v = raw[i];
      if (!v || typeof v !== 'object') continue;
      if (typeof v.vid !== 'number' || !isFinite(v.vid) || v.vid <= 0) continue;
      if (typeof v.pr !== 'string' || v.pr.length === 0) continue;
      out.push({ vid: v.vid, vt: typeof v.vt === 'string' ? v.vt : '', pr: v.pr });
    }
    return out.length >= 2 ? out : null;
  }

  // Cart-variant dedup for the picker (2026-07-21): with the picker live, a product already in
  // the cart at ONE variant can still be offered for its OTHERS — but we never re-show a variant
  // the shopper already has. Returns a shallow clone with cart-owned variants trimmed from `vv`
  // (re-pointing the default vid/price when the offered SKU itself is owned), or null when every
  // offerable variant is already in the cart. cartVids is a { "<variantId>": true } set.
  function pruneVariants(o, cartVids) {
    cartVids = cartVids || {};
    if (o.vid != null && !cartVids[String(o.vid)]) {
      // Offered SKU not owned — keep it as the default; just trim owned variants from the picker.
      if (!Array.isArray(o.vv)) return o;
      var vv = o.vv.filter(function (v) { return v && v.vid != null && !cartVids[String(v.vid)]; });
      return vv.length === o.vv.length ? o : Object.assign({}, o, { vv: vv });
    }
    // Offered SKU is already in the cart — re-default to the best remaining variant, if any.
    var opts = variantOptions(o);
    if (!opts) return null;                                  // single-variant and owned -> drop
    var remaining = opts.filter(function (v) { return !cartVids[String(v.vid)]; });
    if (!remaining.length) return null;                      // owns every variant -> drop
    var d = remaining[0];                                    // offered-first order preserved
    return Object.assign({}, o, { vid: d.vid, pr: d.pr, vv: remaining });
  }

  // <select> of variants; default = the offered SKU (o.vid). Same copy as checkout/TY:
  // "<variant title> — <price>". data-pr carries the raw price so the change handler can
  // re-render the card's main price line to follow the pick.
  function variantSelectHtml(mount, o, opts) {
    var money = mount.getAttribute('data-money-format') || '${{amount}} USD';
    var rows = opts.map(function (v) {
      var sel = (v.vid === o.vid) ? ' selected' : '';
      return '<option value="' + v.vid + '" data-pr="' + escapeHtml(v.pr) + '"' + sel + '>' +
        escapeHtml((v.vt || 'Default') + ' — ' + fmtMoney(v.pr, money)) +
      '</option>';
    }).join('');
    return '<select class="bsf-cu__variant" data-bsf-cu-variant aria-label="' + escapeHtml((o.t || 'Item') + ': choose an option') + '">' + rows + '</select>';
  }

  function cardHtml(mount, o) {
    var money = mount.getAttribute('data-money-format') || '${{amount}} USD';
    var cta = mount.getAttribute('data-cta') || 'Add to cart';
    var opts = variantOptions(o);
    var img = o.img
      ? '<img class="bsf-cu__img" src="' + escapeHtml(o.img) + '" alt="" loading="lazy" width="64" height="64">'
      : '<span class="bsf-cu__img bsf-cu__img--empty" aria-hidden="true"></span>';
    return '' +
      '<div class="bsf-cu__card" data-bsf-cu-card>' +
        '<span class="bsf-cu__thumb" aria-hidden="true">' + img + '</span>' +
        '<div class="bsf-cu__body">' +
          '<p class="bsf-cu__title">' + escapeHtml(o.t || '') + '</p>' +
          '<p class="bsf-cu__price" data-bsf-cu-price>' + fmtMoney(o.pr, money) + '</p>' +
          (opts ? variantSelectHtml(mount, o, opts) : '') +
        '</div>' +
        '<button type="button" class="bsf-cu__add global-button global-button--primary" ' +
          'data-bsf-cu-add data-vid="' + o.vid + '" data-offer-id="' + escapeHtml(o.id || '') + '" aria-label="' + escapeHtml('Add ' + (o.t || 'item') + ' to cart') + '">' +
          escapeHtml(cta) +
        '</button>' +
      '</div>';
  }

  function escapeHtml(s) {
    return String(s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  function renderInto(mount, offers) {
    var list = mount.querySelector('[data-bsf-cu-list]');
    var heading = mount.querySelector('[data-bsf-cu-heading]');
    var note = mount.querySelector('[data-bsf-cu-note]');
    if (!list) return;
    if (!offers.length) { mount.hidden = true; return; }
    if (heading) heading.textContent = mount.getAttribute('data-heading') || '';
    if (note) note.textContent = mount.getAttribute('data-note') || '';
    list.innerHTML = offers.map(function (o) { return cardHtml(mount, o); }).join('');
    mount.hidden = false;
  }

  // Persist bucket (+ impressions when treated) to the cart so they ride to the order.
  // Only writes when a value actually changes — avoids churning the cart on every render.
  function writeAttrs(currentAttrs, impIds) {
    var attrs = {};
    if (writtenBucket !== bucket && (currentAttrs[BUCKET_ATTR] || '') !== bucket) attrs[BUCKET_ATTR] = bucket;
    if (bucket === 't') {
      var imp = impIds.join(',');
      if (imp && writtenImp !== imp && (currentAttrs[IMP_ATTR] || '') !== imp) attrs[IMP_ATTR] = imp;
    }
    if (!Object.keys(attrs).length) { writtenBucket = bucket; return; }
    selfUpdate = true;
    $.ajax({
      type: 'POST', url: root() + 'cart/update.js', dataType: 'json',
      contentType: 'application/json', data: JSON.stringify({ attributes: attrs }),
      complete: function () {
        writtenBucket = bucket;
        if (attrs[IMP_ATTR]) writtenImp = attrs[IMP_ATTR];
      }
    });
  }

  function resolveBucket(cfg, mount) {
    if (bucket) return bucket;
    var custId = String(mount.getAttribute('data-customer-id') || '').trim();
    var key = custId || (window.__bsfCartToken || '');
    if (!key) return null;   // no stable key yet (guest before cart.js) — caller retries
    var holdoutBps = (cfg.holdoutPct || 0) * 100;
    var rolloutBps = (cfg.rolloutPct || 0) * 100;
    if (underBps(key, CART_EXPERIMENT_KEY, holdoutBps)) { bucket = 'h'; return bucket; }
    bucket = underBps(key, CART_ROLLOUT_KEY, rolloutBps) ? 't' : 'h';
    return bucket;
  }

  function run(cart) {
    var all = mounts();
    if (!all.length) return;
    window.__bsfCartToken = (cart && cart.token) || window.__bsfCartToken;

    var pids = (cart && cart.items ? cart.items.map(function (i) { return i.product_id; }) : [])
      .filter(function (v, i, a) { return a.indexOf(v) === i; });
    var vids = {};   // cart-owned variant ids, for the picker's cart-variant dedup
    if (cart && cart.items) cart.items.forEach(function (i) { if (i.variant_id != null) vids[String(i.variant_id)] = true; });
    var attrs = (cart && cart.attributes) || {};

    all.forEach(function (mount) {
      var cfg = readConfig(mount);
      if (!cfg || !cfg.enabled) { mount.hidden = true; return; }

      var b = resolveBucket(cfg, mount);
      if (b == null) return;                       // key not ready — a later ajaxComplete retries
      if (b !== 't') { mount.hidden = true; writeAttrs(attrs, []); return; }

      var max = parseInt(mount.getAttribute('data-max'), 10) || 3;
      var offers = selectOffers(mount, pids, vids, max);
      renderInto(mount, offers);
      writeAttrs(attrs, offers.map(function (o) { return o.id; }));
    });
  }

  function refresh() {
    $.getJSON(root() + 'cart.js', run);
  }

  // ── add-to-cart (in-place, no reload) ─────────────────────────────────────────
  // Two paths, each reusing the theme so we never rebuild its cart markup by hand:
  //  • cart PAGE → Section Rendering: /cart/add.js returns the re-rendered cart section
  //    and we swap it in. Theme cart events are delegated on <body> (app.js.liquid), so
  //    the fresh DOM's quantity/remove keep working; scroll position is kept.
  //  • mini-cart → hand the add to the theme's own delegated `.ajax-submit` flow, which
  //    POSTs the add and calls refreshCart() to rebuild the drawer (stays open). Our line
  //    property rides along as properties[_bsf_upsell_cart].
  // Either way our ajaxComplete handler re-renders the cards (the added item dedups out).
  // Anything unexpected falls back to a full reload — correct, just less smooth.
  function lineProps(offerId) { var p = {}; p[LINE_ATTR] = offerId; return p; }

  function cartSectionId() {
    var el = document.querySelector('[data-section-type="cart-template"]');
    var id = el && el.getAttribute('data-section-id');
    return id && id.length ? id : null;
  }

  function swapSection(id, html) {
    if (!id || typeof html !== 'string') return false;
    var wrapper = document.getElementById('shopify-section-' + id);
    if (!wrapper) return false;
    try {
      var parsed = new DOMParser().parseFromString(html, 'text/html');
      var fresh = parsed.getElementById('shopify-section-' + id);
      wrapper.innerHTML = fresh ? fresh.innerHTML : (parsed.body ? parsed.body.innerHTML : html);
      return true;
    } catch (e) { return false; }
  }

  // Mini-cart: reuse the theme's delegated `.ajax-submit` handler (app.js) — it serializes
  // a hidden form, POSTs /cart/add.js, then refreshCart() rebuilds the drawer. On a non-cart
  // page (where the drawer lives) it never hits the theme's cart-page reload branch.
  function addViaThemeAjaxCart(vid, offerId) {
    // Route through the theme's delegated `.ajax-submit` ONLY when it is present AND safe:
    //  • the ajax mini-cart must be on the page (its item container);
    //  • NOT on the cart page (body.cart): there the theme handler ALSO does a native form.submit()
    //    (full reload) on top of its ajax add → a DOUBLE add + reload. The page-context mount owns
    //    adds on the cart page anyway, so fall through to the Section-Rendering path;
    //  • the store must be in ajax-cart mode — otherwise the theme binds no `.ajax-submit` handler and
    //    the click would hit nothing (silent lost add). (cart_action may be absent on theme_settings;
    //    only bail when it is present AND not 'ajax'.)
    if (!document.querySelector('.js-cart_items')) return false;
    if (document.body && document.body.classList.contains('cart')) return false;
    var ts = window.PXUTheme && window.PXUTheme.theme_settings;
    if (ts && ts.cart_action && ts.cart_action !== 'ajax') return false;
    try {
      var esc = function (v) { return String(v).replace(/"/g, '&quot;'); };
      var form = document.createElement('form');
      form.action = root() + 'cart/add';
      form.method = 'post';
      form.style.display = 'none';
      form.innerHTML =
        '<input type="hidden" name="id" value="' + esc(vid) + '">' +
        '<input type="hidden" name="quantity" value="1">' +
        '<input type="hidden" name="properties[' + LINE_ATTR + ']" value="' + esc(offerId) + '">' +
        '<button type="button" class="ajax-submit"></button>';
      document.body.appendChild(form);
      $(form).find('.ajax-submit').trigger('click');
      setTimeout(function () { if (form.parentNode) form.parentNode.removeChild(form); }, 100);
      return true;
    } catch (e) { return false; }
  }

  function addOffer(btn) {
    var vid = btn.getAttribute('data-vid');
    var offerId = btn.getAttribute('data-offer-id');
    if (!vid || btn.disabled) return;
    var mount = btn.closest('[data-bsf-cu]');
    var isMini = !!mount && mount.getAttribute('data-context') === 'mini';
    btn.disabled = true;
    if (mount) btn.textContent = mount.getAttribute('data-added') || 'Added';

    function fail() {
      btn.disabled = false;
      if (mount) btn.textContent = mount.getAttribute('data-cta') || 'Add to cart';
    }

    // Mini-cart: the theme refreshes the drawer for us (no reload).
    if (isMini && addViaThemeAjaxCart(vid, offerId)) return;

    // Cart page: Section Rendering swap; reload only when it isn't available.
    var sid = cartSectionId();
    var body = { items: [{ id: vid, quantity: 1, properties: lineProps(offerId) }] };
    if (sid) { body.sections = [sid]; body.sections_url = '/cart'; }
    $.ajax({
      type: 'POST', url: root() + 'cart/add.js', dataType: 'json',
      contentType: 'application/json', data: JSON.stringify(body),
      success: function (res) {
        var html = sid && res && res.sections ? res.sections[sid] : null;
        if (!swapSection(sid, html)) window.location.reload();
        // else our ajaxComplete handler re-renders the cards into the fresh mount.
      },
      error: fail,
    });
  }

  // Variant pick: point the Add button at the chosen variant and update the shown price.
  // The offer id (data-offer-id) is unchanged — it identifies the OFFER, not the variant,
  // so measurement (impressions / accepted line property) is variant-agnostic by design.
  function chooseVariant(sel) {
    var card = sel.closest('[data-bsf-cu-card]');
    if (!card) return;
    var opt = sel.options[sel.selectedIndex];
    var btn = card.querySelector('[data-bsf-cu-add]');
    if (btn && sel.value) btn.setAttribute('data-vid', sel.value);
    var priceEl = card.querySelector('[data-bsf-cu-price]');
    var pr = opt && opt.getAttribute('data-pr');
    if (priceEl && pr != null) {
      var mount = sel.closest('[data-bsf-cu]');
      var money = (mount && mount.getAttribute('data-money-format')) || '${{amount}} USD';
      priceEl.textContent = fmtMoney(pr, money);
    }
  }

  // ── wire-up ─────────────────────────────────────────────────────────────────
  $(document).on('click', '[data-bsf-cu-add]', function (e) { e.preventDefault(); addOffer(this); });
  $(document).on('change', '[data-bsf-cu-variant]', function () { chooseVariant(this); });

  // The page carries TWO jQuery copies: the theme's add-to-cart and cart-page quantity changes (app.js) run on
  // window.$, which is NOT window.jQuery, and ajaxComplete fires only on the copy that made the request
  // (verified live 2026-09-30). Listen on each distinct copy; a request fires on exactly one, so nothing doubles.
  // Our own add and cart/update go through window.jQuery, so the selfUpdate guard still sees them.
  [window.jQuery, window.$].filter(function (jq, i, all) {
    return jq && typeof jq.fn === 'object' && all.indexOf(jq) === i;
  }).forEach(function (jq) { jq(document).ajaxComplete(onCartAjax); });

  function onCartAjax(e, xhr, settings) {
    var url = (settings && settings.url) || '';
    if (url.indexOf('/cart') === -1) return;
    if (selfUpdate && url.indexOf('/cart/update') !== -1) { selfUpdate = false; return; }
    if (!document.querySelector('[data-bsf-cu]')) return;
    var data = xhr && xhr.responseJSON;
    if (data && typeof data.token !== 'undefined' && data.items) run(data);
    else refresh();     // add/change.js returns a line item, not the cart
  }

  if (document.querySelector('[data-bsf-cu]')) {
    if (document.readyState !== 'loading') refresh();
    else document.addEventListener('DOMContentLoaded', refresh);
  }
})();
